/**
 * Deals-local helpers for deriving saved-view column sets from
 * per-field `savedViewMembership` declarations.
 *
 * Centralizing this here keeps `deal-saved-views.ts` free of hardcoded
 * `visibleColumns` arrays — each field's declaration documents which
 * saved views include it.
 *
 * Order of returned ids follows the field declaration order in `fields`
 * (i.e. `Object.keys(DEAL_FIELDS)`). Saved-view consumers in the Hub
 * (see `UniversalEntityPageContent.tsx`) use `.includes(fieldId)` for
 * membership checks — ordering is not semantically significant.
 */
import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";

/**
 * Returns the ordered list of field ids whose `savedViewMembership`
 * includes `viewId`. `TId` is a literal field-id union (e.g. `DealFieldId`)
 * so the returned array is compile-time compatible with
 * `SavedViewConfig<TId>["visibleColumns"]`.
 *
 * Field declaration order in `fields` determines result order.
 */
export function collectFieldsForSavedView<TId extends string>(
  viewId: string,
  fields: Readonly<Record<TId, SimpleFieldConfig>>
): TId[] {
  const result: TId[] = [];
  for (const key of Object.keys(fields) as TId[]) {
    const membership = fields[key].savedViewMembership;
    if (membership && membership.includes(viewId)) {
      result.push(key);
    }
  }
  return result;
}
